import React, { useEffect, useCallback, useState, useRef } from 'react';

import * as Yup from 'yup';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import { Button, Row, Col, Container } from 'react-bootstrap';
import { addMinutes, parseISO, format } from 'date-fns';

import { useToast } from '../../hooks/toast';
import { useAgendamento } from '../../hooks/agendamento';

import api from '../../services/api';

import SelectSync from '../SelectSync';
import Input from '../Input';
import Loading from '../Loading';

import {
  CONFIRMACAO_AGENDA,
  optionsHorasAtendimento,
  STATUS_AGENDA,
  TIPO_PERFIL,
} from '../../utils/funcoes';
import getValidationErrors from '../../utils/getValidationErrors';
import getResponseErrors from '../../utils/getResponseErrors';
import { IOrcamento } from '../../pages/PacienteAtendimento/Odonto';
import { IAgendamento } from '../Agendamento';

interface IProcedimento {
  ProCodigo: number;
  ProValor: number;
  ProNome: string;
  ProDuracao: number;
}

interface IFormData {
  AgeData: Date;
  AgeHora: string;
}

export interface IReturnAgendamentoTratamento {
  AgeCodigo: number;
  AgeInicio: Date;
  AgeInicioFormatado: string;
  AgeConfCodigo: number;
  updated_at: Date;
  valor: number;
}

interface IDadosAgendamento {
  ConCodigo: number;
  AgeClinica: number;
}

interface IAgendamentoAdicionado extends IAgendamento {
  AgeCodigo: number;
}

type ComponentProps = {
  orcamentoId: number;
  handleFechar(dados: IReturnAgendamentoTratamento): void;
};

const TratamentoAgendamento: React.FC<ComponentProps> = ({
  orcamentoId,
  handleFechar,
}) => {
  const formRef = useRef<FormHandles>(null);
  const { addToast } = useToast();
  const { buscaProcedimentos } = useAgendamento();
  const [loading, setLoading] = useState(false);
  const [orcamento, setOrcamento] = useState<IOrcamento>();
  const [procedimento, setProcedimento] = useState<IProcedimento | undefined>();

  const [finalizado, setFinalizado] = useState(false);
  const [dadosAgendamento, setDadosAgendamento] = useState<
    IDadosAgendamento | undefined
  >(undefined);
  const [optionsHora, _] = useState(() => optionsHorasAtendimento());
  const initialData = { AgeData: format(new Date(), 'yyyy-MM-dd') };

  useEffect(() => {
    async function loadDados(): Promise<void> {
      setLoading(true);
      try {
        const response = await api.get<IOrcamento>(`tratamento/${orcamentoId}`);
        const dadosOrcamento = response.data;
        if (dadosOrcamento.finalizado) {
          setFinalizado(true);
        } else {
          // selecionar confCodigo baseado na agenda

          const responseAgendamento = await api.get<IAgendamento>(
            `/agendamentos/${dadosOrcamento.AgeCodigo}/detalhe`,
          );

          setDadosAgendamento({
            ConCodigo: responseAgendamento.data.AgeConfCodigo,
            AgeClinica: responseAgendamento.data.AgeClinica,
          });

          const responsePerfis = await api.get<number[]>(
            `/agendas/${responseAgendamento.data.AgeConfCodigo}/perfis`,
          );

          const respostaPro = await buscaProcedimentos(
            responsePerfis.data.join(','),
          );

          if (respostaPro) {
            const tratamento = respostaPro.procedimentos.find(proc =>
              proc.ProNome.toUpperCase().includes('TRATAMENTO CONTRATADO'),
            );
            setProcedimento(tratamento);
          }
        }

        setOrcamento(response.data);
      } catch (error) {
        const mensagemErro = getResponseErrors(error);
        addToast({
          title: 'Não foi possível carregar disponibilidade',
          type: 'error',
          description: mensagemErro,
        });
      } finally {
        setLoading(false);
      }
    }
    if (orcamentoId) loadDados();
  }, [orcamentoId, buscaProcedimentos, addToast]);

  // const total = orcamento?.valor || 0;
  // const totalAgendado = orcamento?.agendamentosTratamento.reduce((tot, age) => {
  //   return (
  //     tot +
  //     age.agenda.procedimentosValorCobrado.reduce((tot2, proc) => {
  //       return tot2 + Number(proc.ProValor);
  //     }, 0)
  //   );
  // }, 0);

  const handleSubmit = useCallback(
    async (dados: IFormData) => {
      setLoading(true);
      try {
        if (!orcamento) throw new Error('Orçamento não encontrado');
        if (!procedimento)
          throw new Error('Procedimento Tratamento não encontrados');
        if (!dadosAgendamento)
          throw new Error('dados para agendamento não encontrados');

        formRef.current?.setErrors({});
        const schema = Yup.object().shape({
          AgeData: Yup.date().required(),
          AgeHora: Yup.string().required('Hora obrigatório'),
        });

        await schema.validate(dados, {
          abortEarly: false,
        });

        const dataAgendamento = parseISO(
          `${dados.AgeData} ${dados.AgeHora}:00`,
        );

        const agendamento = {
          AgeConfCodigo: dadosAgendamento.ConCodigo,
          AgeInicio: dataAgendamento,
          AgeFim: addMinutes(dataAgendamento, procedimento.ProDuracao),
          AgePacCodigo: orcamento.PacCodigo,
          AgeFormaPag: 9,
          AgeClinica: dadosAgendamento.AgeClinica,
          AgeObs: '',
          AgeStatus: STATUS_AGENDA.AGENDADO,
          AgeConfirmado: CONFIRMACAO_AGENDA.AGUARDANDO_CONFIRMACAO,
          AgePlano: orcamento.plano,
          AgeTipo: TIPO_PERFIL.ODONTOLOGIA,
          AgeTratamento: 1,
          duracao: procedimento.ProDuracao,
          procedimentos: [
            {
              ProCodigo: procedimento.ProCodigo,
              ProNome: procedimento.ProNome,
              ProValor: parseFloat(
                (orcamento.valor / orcamento.secoes).toFixed(2),
              ),
            },
          ],
        };

        const agendamentoAdicionado = await api.post<IAgendamentoAdicionado>(
          `/agendamentos/tratamento/${orcamento.id}`,
          agendamento,
        );
        // pode pegar o retorno e jogar para a agenda

        addToast({ title: 'Agendamento salvo', type: 'success' });

        const valorAgendamento = agendamentoAdicionado.data.procedimentos.reduce(
          (tot, proc) => {
            return tot + Number(proc.ProValorCobrado);
          },
          0,
        );

        handleFechar({
          AgeCodigo: agendamentoAdicionado.data.AgeCodigo,
          AgeInicio: agendamentoAdicionado.data.AgeInicio,
          AgeInicioFormatado: format(
            parseISO(agendamentoAdicionado.data.AgeInicio.toString()),
            'dd/MM/yyyy HH:mm',
          ),
          AgeConfCodigo: agendamentoAdicionado.data.AgeConfCodigo,
          updated_at: agendamentoAdicionado.data.updated_at,
          valor: valorAgendamento,
        });
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);

          formRef.current?.setErrors(errors);
          return;
        }
        const mensagemErro = getResponseErrors(error);
        addToast({
          type: 'error',
          title: 'Houve um erro ao salvar agendamento',
          description: mensagemErro,
          timeAlive: 10000,
        });
      } finally {
        setLoading(false);
      }
    },
    [addToast, orcamento, procedimento, dadosAgendamento, handleFechar],
  );

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const handleOnChangeVazio = useCallback(() => {}, []);

  return (
    <Container className="my-4">
      {loading && <Loading />}

      {finalizado ? (
        <h5 className="text-center">Status: Pedido de Exame Finalizado</h5>
      ) : (
        <>
          <h5>Gerar agendamento</h5>
          <Form ref={formRef} initialData={initialData} onSubmit={handleSubmit}>
            <Row className="mb-2">
              <Col md={3} sm={6}>
                <Input name="AgeData" label="Data" type="date" />
              </Col>
              <Col md={3} sm={6}>
                <SelectSync
                  name="AgeHora"
                  label="Hora"
                  placeholder="Selecione"
                  isClearable={false}
                  handleSelectChange={handleOnChangeVazio}
                  options={optionsHora}
                />
              </Col>
              <Col md={2} sm={6}>
                <br />
                <Button
                  type="submit"
                  size="sm"
                  className="mt-1"
                  disabled={loading}
                >
                  Gerar
                </Button>
              </Col>
            </Row>
          </Form>
        </>
      )}
    </Container>
  );
};

export default TratamentoAgendamento;
